import { useMemo, useCallback } from 'react';
import useTablaPrefs from './useTablaPrefs';
import { esFiltroActivo, limpiarFiltros } from './filtros';

export const DENSIDADES = ['s', 'm', 'l'];

/**
 * Estado del estándar de tablas de ADMIN: orden, filtros por columna, anchos,
 * orden y visibilidad de columnas y densidad, todo persistido por usuario.
 *
 * `columnas`: [{ id, label, width, minWidth, sortable, filter: 'text'|'multi'|'date',
 *   defaultVisible, fixed }]. Una columna `fixed` no se puede ocultar ni mover
 * (p. ej. Acciones).
 */
const useTablaEstandar = ({ tabla, columnas }) => {
  const { prefs, ready, actualizar, restablecer } = useTablaPrefs(tabla);

  const porId = useMemo(() => Object.fromEntries(columnas.map((c) => [c.id, c])), [columnas]);

  const sortBy = prefs.sortBy && porId[prefs.sortBy] ? prefs.sortBy : null;
  const sortDir = sortBy ? prefs.sortDir || 'asc' : null;
  const density = DENSIDADES.includes(prefs.density) ? prefs.density : 'm';

  const filtros = useMemo(() => {
    const crudos = prefs.filters || {};
    return limpiarFiltros(
      Object.fromEntries(Object.entries(crudos).filter(([id]) => porId[id]?.filter)),
    );
  }, [prefs.filters, porId]);

  const ocultas = useMemo(() => {
    if (Array.isArray(prefs.hidden)) return new Set(prefs.hidden);
    return new Set(columnas.filter((c) => c.defaultVisible === false).map((c) => c.id));
  }, [prefs.hidden, columnas]);

  // Orden guardado + columnas nuevas (que no estaban al guardar) al final, las fijas siempre al final.
  const orden = useMemo(() => {
    const guardado = (prefs.columnOrder || []).filter((id) => porId[id] && !porId[id].fixed);
    const resto = columnas.filter((c) => !c.fixed && !guardado.includes(c.id)).map((c) => c.id);
    return [...guardado, ...resto, ...columnas.filter((c) => c.fixed).map((c) => c.id)];
  }, [prefs.columnOrder, columnas, porId]);

  const columnasVisibles = useMemo(
    () => orden.map((id) => porId[id]).filter((c) => c.fixed || !ocultas.has(c.id)),
    [orden, porId, ocultas],
  );

  const anchos = useMemo(() => prefs.widths || {}, [prefs.widths]);

  // Panel superior (Filtros) abierto o plegado; null = el usuario aún no ha elegido.
  const panelAbierto = typeof prefs.panelOpen === 'boolean' ? prefs.panelOpen : null;
  const fijarPanelAbierto = useCallback(
    (abierto) => actualizar({ panelOpen: abierto }),
    [actualizar],
  );

  // Lo que viaja al backend. La clave estable evita refetch por cambios de identidad.
  // Solo se piden las columnas visibles: la tabla tiene más de 130 y no todas hacen falta.
  const idsVisibles = columnasVisibles
    .filter((c) => !c.fixed)
    .map((c) => c.id)
    .sort()
    .join(',');
  const consultaKey = JSON.stringify({ sortBy, sortDir, filtros, idsVisibles });
  const consulta = useMemo(
    () => ({
      sortBy: sortBy || undefined,
      sortDir: sortDir || undefined,
      colFilters: filtros,
      columnas: idsVisibles || undefined,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [consultaKey],
  );

  const ordenar = useCallback(
    (id) => {
      if (!porId[id]?.sortable) return;
      // asc → desc → sin orden
      if (sortBy !== id) actualizar({ sortBy: id, sortDir: 'asc' });
      else if (sortDir === 'asc') actualizar({ sortBy: id, sortDir: 'desc' });
      else actualizar({ sortBy: null, sortDir: null });
    },
    [porId, sortBy, sortDir, actualizar],
  );

  // Orden elegido desde un selector ('columna:dir' o '' para quitarlo), p. ej. en móvil.
  const fijarOrdenDirecto = useCallback(
    (valor) => {
      const [id, dir] = valor.split(':');
      if (!id || !porId[id]?.sortable) actualizar({ sortBy: null, sortDir: null });
      else actualizar({ sortBy: id, sortDir: dir === 'desc' ? 'desc' : 'asc' });
    },
    [porId, actualizar],
  );

  const fijarFiltro = useCallback(
    (id, valor) => {
      const siguiente = { ...filtros };
      if (esFiltroActivo(valor)) siguiente[id] = valor;
      else delete siguiente[id];
      actualizar({ filters: siguiente });
    },
    [filtros, actualizar],
  );

  const limpiarTodosFiltros = useCallback(() => actualizar({ filters: {} }), [actualizar]);

  const fijarAncho = useCallback(
    (id, ancho) =>
      actualizar({ widths: { ...anchos, [id]: Math.min(1200, Math.max(40, Math.round(ancho))) } }),
    [anchos, actualizar],
  );

  const fijarOrden = useCallback((nuevo) => actualizar({ columnOrder: nuevo }), [actualizar]);

  const alternarColumna = useCallback(
    (id) => {
      const siguiente = new Set(ocultas);
      if (siguiente.has(id)) siguiente.delete(id);
      else siguiente.add(id);
      actualizar({ hidden: [...siguiente] });
    },
    [ocultas, actualizar],
  );

  // Muestra la columna y fija el orden en un solo guardado (dos `actualizar` seguidos se pisarían).
  const mostrarConOrden = useCallback(
    (id, nuevoOrden) => {
      const siguiente = new Set(ocultas);
      siguiente.delete(id);
      actualizar({ hidden: [...siguiente], columnOrder: nuevoOrden });
    },
    [ocultas, actualizar],
  );

  const fijarDensidad = useCallback((d) => actualizar({ density: d }), [actualizar]);

  const restablecerColumnas = useCallback(
    () => actualizar({ columnOrder: [], hidden: undefined, widths: {}, density: 'm' }),
    [actualizar],
  );

  const filtrosActivos = useMemo(
    () => Object.entries(filtros).map(([id, f]) => ({ id, label: porId[id].label, filtro: f })),
    [filtros, porId],
  );

  return {
    ready,
    columnas,
    columnasVisibles,
    orden,
    ocultas,
    anchos,
    panelAbierto,
    fijarPanelAbierto,
    density,
    sortBy,
    sortDir,
    filtros,
    filtrosActivos,
    consulta,
    ordenar,
    fijarOrdenDirecto,
    fijarFiltro,
    limpiarTodosFiltros,
    fijarAncho,
    fijarOrden,
    alternarColumna,
    mostrarConOrden,
    fijarDensidad,
    restablecerColumnas,
    restablecerTodo: restablecer,
  };
};

export default useTablaEstandar;
